/**
 * Vendor Comparison Template Form
 * Vendor evaluation and selection matrix
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const VendorComparisonForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    project_name: '',
    company_name: '',
    date: new Date().toISOString().split('T')[0],
    prepared_by: '',
    vendor_names: ['Vendor A', 'Vendor B', 'Vendor C'],
    evaluation_criteria: [{
      criteria: '', weight: 20, vendor_1_score: 0, vendor_2_score: 0, vendor_3_score: 0, vendor_4_score: 0, notes: ''
    }],
    vendor_details: [],
    pricing_comparison: [],
    pros_cons: [],
    recommendation: '',
    rai_assignments: [],
    followups: []
  });

  const getSampleData = () => ({
    project_name: 'Enterprise CRM Selection',
    company_name: 'Acme Corp',
    date: new Date().toISOString().split('T')[0],
    prepared_by: 'Procurement Team',
    vendor_names: ['Salesforce', 'HubSpot', 'Microsoft Dynamics', 'Zoho CRM'],
    evaluation_criteria: [
      { criteria: 'Feature Completeness', weight: 25, vendor_1_score: 9, vendor_2_score: 7, vendor_3_score: 8, vendor_4_score: 6, notes: 'Core CRM features' },
      { criteria: 'Ease of Use / UX', weight: 20, vendor_1_score: 7, vendor_2_score: 9, vendor_3_score: 6, vendor_4_score: 8, notes: 'User adoption critical' },
      { criteria: 'Integration Capabilities', weight: 15, vendor_1_score: 9, vendor_2_score: 8, vendor_3_score: 9, vendor_4_score: 6, notes: 'Needs to integrate with ERP' },
      { criteria: 'Scalability', weight: 15, vendor_1_score: 10, vendor_2_score: 7, vendor_3_score: 9, vendor_4_score: 7, notes: 'Growth projection: 3x users in 2 years' },
      { criteria: 'Total Cost of Ownership', weight: 15, vendor_1_score: 5, vendor_2_score: 8, vendor_3_score: 6, vendor_4_score: 9, notes: '3-year TCO analysis' },
      { criteria: 'Vendor Support & Training', weight: 10, vendor_1_score: 8, vendor_2_score: 8, vendor_3_score: 7, vendor_4_score: 7, notes: '24/7 support required' }
    ],
    vendor_details: [
      { vendor: 'Salesforce', contact: 'John Smith', email: 'john@salesforce.com', phone: '+1-555-0101', pricing: '$150/user/month' },
      { vendor: 'HubSpot', contact: 'Jane Doe', email: 'jane@hubspot.com', phone: '+1-555-0102', pricing: '$90/user/month' },
      { vendor: 'Microsoft Dynamics', contact: 'Bob Wilson', email: 'bob@microsoft.com', phone: '+1-555-0103', pricing: '$115/user/month' },
      { vendor: 'Zoho CRM', contact: 'Alice Brown', email: 'alice@zoho.com', phone: '+1-555-0104', pricing: '$40/user/month' }
    ],
    pricing_comparison: [
      { item: 'Per User License', vendor_1: '$150/mo', vendor_2: '$90/mo', vendor_3: '$115/mo', vendor_4: '$40/mo' },
      { item: 'Implementation', vendor_1: '$50,000', vendor_2: '$20,000', vendor_3: '$35,000', vendor_4: '$10,000' },
      { item: 'Annual Support', vendor_1: 'Included', vendor_2: 'Included', vendor_3: '$5,000', vendor_4: 'Included' },
      { item: '3-Year TCO (100 users)', vendor_1: '$590,000', vendor_2: '$344,000', vendor_3: '$449,000', vendor_4: '$154,000' }
    ],
    pros_cons: [
      { vendor: 'Salesforce', pros: 'Market leader, extensive features, strong ecosystem', cons: 'High cost, complex implementation' },
      { vendor: 'HubSpot', pros: 'User-friendly, good marketing integration, fair pricing', cons: 'Less customizable, limited enterprise features' },
      { vendor: 'Microsoft Dynamics', pros: 'Good Office 365 integration, familiar interface', cons: 'Steep learning curve, moderate pricing' },
      { vendor: 'Zoho CRM', pros: 'Very affordable, decent features', cons: 'Limited scalability, smaller ecosystem' }
    ],
    recommendation: 'Based on the weighted evaluation, we recommend HubSpot as the primary choice with Salesforce as a premium alternative. HubSpot offers the best balance of usability, features, and total cost of ownership for our current needs. However, if long-term scalability is prioritized over cost, Salesforce should be considered. We recommend scheduling demos with both vendors before final decision.',
    rai_assignments: [
      { task: 'Schedule vendor demos', responsible: 'Procurement Lead', accountable: 'IT Director', informed: 'Sales VP, CFO', due_date: '2025-02-01', status: 'Pending' },
      { task: 'Negotiate contract terms', responsible: 'Legal Counsel', accountable: 'CFO', informed: 'IT Director', due_date: '2025-02-15', status: 'Not Started' }
    ],
    followups: [
      { item: 'Complete security questionnaire for shortlisted vendors', owner: 'IT Security', due_date: '2025-01-25', priority: 'High', status: 'Pending' },
      { item: 'Gather user feedback from pilot team', owner: 'Sales Manager', due_date: '2025-02-10', priority: 'Medium', status: 'Pending' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Vendor Comparison Draft";
      const payload = {
        name: draftName,
        template_type: "Vendor Comparison",
        template_id: "vendor-comparison",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "vendor-comparison", "Vendor Comparison", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("vendor-comparison", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/vendor-comparison`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Vendor_Comparison_${formData.project_name.replace(/\s+/g, '_') || 'Document'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating document.");
    }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Vendor Comparison",
        formData,
        settings: activeSettings || {},
        templateType: "Vendor Comparison",
        templateId: "vendor-comparison",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const addVendor = () => {
    const newVendorName = `Vendor ${formData.vendor_names.length + 1}`;
    setFormData({ ...formData, vendor_names: [...formData.vendor_names, newVendorName] });
  };

  const updateVendorName = (index, value) => {
    const names = [...formData.vendor_names];
    names[index] = value;
    setFormData({ ...formData, vendor_names: names });
  };

  const removeVendor = (index) => {
    if (formData.vendor_names.length > 2) {
      setFormData({ ...formData, vendor_names: formData.vendor_names.filter((_, i) => i !== index) });
    }
  };

  const addCriteria = () => {
    setFormData({
      ...formData,
      evaluation_criteria: [...formData.evaluation_criteria, {
        criteria: '', weight: 10, vendor_1_score: 0, vendor_2_score: 0, vendor_3_score: 0, vendor_4_score: 0, notes: ''
      }]
    });
  };

  const updateCriteria = (index, field, value) => {
    const criteria = [...formData.evaluation_criteria];
    criteria[index] = { ...criteria[index], [field]: value };
    setFormData({ ...formData, evaluation_criteria: criteria });
  };

  const removeCriteria = (index) => {
    setFormData({ ...formData, evaluation_criteria: formData.evaluation_criteria.filter((_, i) => i !== index) });
  };

  const calculateWeightedScore = (vendorIndex) => {
    let totalScore = 0;
    let totalWeight = 0;
    formData.evaluation_criteria.forEach(c => {
      const score = c[`vendor_${vendorIndex + 1}_score`] || 0;
      const weight = c.weight || 0;
      totalScore += score * weight;
      totalWeight += weight;
    });
    return totalWeight > 0 ? (totalScore / totalWeight).toFixed(1) : '0.0';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Vendor Comparison</h1>
                <p className="text-sm text-gray-500">Vendor evaluation and selection matrix</p>
              </div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Evaluation Information</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2"><Label>Project Name *</Label><Input value={formData.project_name} onChange={(e) => setFormData({...formData, project_name: e.target.value})} placeholder="CRM Vendor Selection" /></div>
              <div><Label>Company</Label><Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} placeholder="Company" /></div>
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Prepared By</Label><Input value={formData.prepared_by} onChange={(e) => setFormData({...formData, prepared_by: e.target.value})} placeholder="Procurement Team" /></div>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold">Vendors Being Evaluated</Label>
              <p className="text-xs text-gray-500 mb-2">Add vendors to compare (minimum 2, maximum 4)</p>
              <div className="flex flex-wrap gap-2">
                {formData.vendor_names.map((name, idx) => (
                  <div key={idx} className="flex items-center gap-1 bg-blue-50 rounded-lg p-2">
                    <Input 
                      value={name} 
                      onChange={(e) => updateVendorName(idx, e.target.value)} 
                      className="w-32 h-8 text-sm"
                    />
                    {formData.vendor_names.length > 2 && (
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeVendor(idx)}>
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
                {formData.vendor_names.length < 4 && (
                  <Button variant="outline" size="sm" onClick={addVendor}><Plus className="h-4 w-4 mr-1" /> Add Vendor</Button>
                )}
              </div>
            </div>

            <Separator />

            <div>
              <div className="flex justify-between items-center mb-3">
                <div>
                  <Label className="font-semibold text-lg">Evaluation Criteria</Label>
                  <p className="text-xs text-gray-500">Score each vendor 1-10 for each criterion. Weights should total 100%.</p>
                </div>
                <div className="flex gap-4">
                  {formData.vendor_names.map((name, idx) => (
                    <div key={idx} className="text-center bg-blue-100 rounded-lg px-3 py-1">
                      <div className="text-xs text-gray-600">{name}</div>
                      <div className="text-lg font-bold text-blue-700">{calculateWeightedScore(idx)}</div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-2 text-left text-sm">Criteria</th>
                      <th className="border p-2 text-center text-sm w-20">Weight %</th>
                      {formData.vendor_names.map((name, idx) => (
                        <th key={idx} className="border p-2 text-center text-sm w-24">{name}</th>
                      ))}
                      <th className="border p-2 text-left text-sm w-40">Notes</th>
                      <th className="border p-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.evaluation_criteria.map((c, idx) => (
                      <tr key={idx}>
                        <td className="border p-1">
                          <Input value={c.criteria} onChange={(e) => updateCriteria(idx, 'criteria', e.target.value)} placeholder="Criterion" className="text-sm h-8" />
                        </td>
                        <td className="border p-1">
                          <Input type="number" min="0" max="100" value={c.weight} onChange={(e) => updateCriteria(idx, 'weight', parseInt(e.target.value) || 0)} className="text-sm h-8 text-center" />
                        </td>
                        {formData.vendor_names.map((_, vIdx) => (
                          <td key={vIdx} className="border p-1">
                            <Input type="number" min="0" max="10" value={c[`vendor_${vIdx + 1}_score`]} onChange={(e) => updateCriteria(idx, `vendor_${vIdx + 1}_score`, parseInt(e.target.value) || 0)} className="text-sm h-8 text-center" />
                          </td>
                        ))}
                        <td className="border p-1">
                          <Input value={c.notes} onChange={(e) => updateCriteria(idx, 'notes', e.target.value)} placeholder="Notes" className="text-sm h-8" />
                        </td>
                        <td className="border p-1 text-center">
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeCriteria(idx)}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button variant="outline" size="sm" onClick={addCriteria} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add Criterion</Button>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold">Recommendation</Label>
              <Textarea 
                value={formData.recommendation} 
                onChange={(e) => setFormData({...formData, recommendation: e.target.value})} 
                placeholder="Based on the evaluation, provide your recommendation..." 
                rows={4}
              />
            </div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default VendorComparisonForm;
